import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router';
import { Button, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import ComplianceTypesModal from './ComplianceTypesModal';
import NewComplianceRequestModal from './NewComplianceRequestModal';
import {
	OVERVIEW_FETCH_PAGINATION,
	OVERVIEW_STATUS_COUNTS_NAME,
	OVERVIEW_TARGET_COLLECTION,
} from './BuyerComplianceOverviewPage';
import ComplianceSettingsModal from './ComplianceSettingsModal';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../../actions/compliance_request_instances_actions';
import { COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS } from '../../actions/compliance_requests_actions';
import {
	complianceRequestsRestCrudThunks,
	fetchComplianceRequestsWithInstanceCounts,
} from '../../thunks/compliance_requests_thunks';
import {
	complianceRequestInstancesRestCrudThunks,
	fetchComplianceSummary,
} from '../../thunks/compliance_request_instances_thunks';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { fetchSupplierFacilitiesInPrivateNetwork } from '../../thunks/supplier_facilities_thunks';
import {
	locationsRestCrudThunks,
	fetchLocationsWithRegionsAndSubRegions,
} from '../../thunks/locations_thunks';
import {
	problemTypesRestCrudThunks,
	problemTypesHeirarchical,
} from '../../thunks/problem_types_thunks';
import posthog from 'posthog-js';
import { withUserType } from '../withUserType';
import { COMPLIANCE_FEATURE_FLAG } from '../compliance_common/ComplianceConstants';
import {
	buildBuyerComplianceOverviewFilterConfig,
	buildComplianceRequestsFilterConfig,
	omitStatusFilters,
} from '../../utils/ComplianceUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

const SUB_TABS = [
	{ key: 'overview', name: 'Overview' },
	{ key: 'requests', name: 'Requests' },
];

// The buyer app and the internal-tech app render this same shell under different
// role-prefixed URLs (/buyer/... vs /supplier/...); the base path is derived from
// the current URL rather than a build constant so sub-tab nav stays on the right
// prefix in either build.
const COMPLIANCE_PATH_MARKER = '/compliance';

const REQUESTS_TARGET_COLLECTION = 'buyerComplianceRequestsIndex';
// Seeded compliance_types collection reused to back the Requests type-filter select.
const TYPES_FILTER_TARGET_COLLECTION = 'complianceAdvancedFilters';
// supplier_facilities collection backing the Overview supplier-filter async select.
const SUPPLIERS_FILTER_TARGET_COLLECTION = 'complianceOverviewSupplierFilter';
// compliance_requests collection backing the Overview request-filter async select.
const REQUESTS_FILTER_TARGET_COLLECTION = 'complianceOverviewRequestFilter';
// locations collection backing the Requests location tree-select filter (seeded in
// locations_reducer).
const LOCATIONS_FILTER_TARGET_COLLECTION = 'complianceRequestsLocationFilter';
// problem_types collection backing the Requests problem-type tree-select filter
// (seeded in problem_types_reducer).
const PROBLEM_TYPES_FILTER_TARGET_COLLECTION = 'complianceRequestsProblemTypeFilter';

const BuyerCompliancePage: FC<any> = ({
	history,
	childRoutes,
	summary,
	complianceRequestInstances,
	complianceRequests,
	complianceTypes,
	supplierFacilities,
	locations,
	problemTypes,
	refetchComplianceRequests,
	refetchOverviewInstances,
	fetchSummary,
	fetchComplianceTypes,
	fetchSupplierFacilities,
	fetchComplianceRequestOptions,
	fetchLocationsForRequestsFilter,
	fetchMultipleLocations,
	fetchProblemTypesForRequestsFilter,
	fetchMultipleProblemTypes,
	updateInstanceFilters,
	clearAndUpdateInstanceFilters,
	updateRequestFilters,
	clearAndUpdateRequestFilters,
}): React.ReactElement => {
	const pathname = history.location.pathname;
	// Everything up to and including '/compliance' — role prefix (/buyer or /supplier)
	// comes from the actual URL, not the build role.
	const markerIndex = pathname.indexOf(COMPLIANCE_PATH_MARKER);
	const basePath =
		markerIndex === -1 ? pathname : pathname.slice(0, markerIndex + COMPLIANCE_PATH_MARKER.length);
	const [typesModalOpen, setTypesModalOpen] = useState(false);
	const [newRequestModalOpen, setNewRequestModalOpen] = useState(false);
	const [settingsModalOpen, setSettingsModalOpen] = useState(false);

	useMountEffect(() => {
		fetchSummary();
		// Prime the Overview filter dropdowns. OWAsyncSelect renders DISABLED while
		// its option list is empty (see OWAsyncSelect's `noVisibleRecordsWithoutSearch`
		// guard), so without options loaded before the Filters popover first opens the
		// Supplier/Request selects look like dead boxes instead of dropdowns. Fetch
		// their lists up front so they're populated (and enabled) on first open.
		fetchSupplierFacilities({}, SUPPLIERS_FILTER_TARGET_COLLECTION, { current: 1, pageSize: 25 });
		fetchComplianceRequestOptions({}, REQUESTS_FILTER_TARGET_COLLECTION, {
			current: 1,
			pageSize: 25,
		});
		// Prime the Requests-tab location + problem-type tree-select collections (same
		// reason as above — the tree-selects render disabled while their record list is
		// empty). Also lets the location/problem-type pills hydrate on load.
		fetchComplianceTypes({}, TYPES_FILTER_TARGET_COLLECTION, { current: 1, pageSize: 25 });
		fetchLocationsForRequestsFilter({}, LOCATIONS_FILTER_TARGET_COLLECTION, {
			current: 1,
			pageSize: 25,
		});
		fetchProblemTypesForRequestsFilter({}, PROBLEM_TYPES_FILTER_TARGET_COLLECTION, {
			current: 1,
			pageSize: 25,
		});
	});

	const openTypesModal = () => setTypesModalOpen(true);
	const closeTypesModal = () => setTypesModalOpen(false);
	const openNewRequestModal = () => setNewRequestModalOpen(true);
	const closeNewRequestModal = () => setNewRequestModalOpen(false);
	const openSettingsModal = () => setSettingsModalOpen(true);
	const closeSettingsModal = () => setSettingsModalOpen(false);

	const handleNewRequestSuccess = () => {
		setNewRequestModalOpen(false);
		refetchComplianceRequests();
		// Publishing a request fans out an instance per targeted supplier, so the
		// Overview grid and its pipeline chips are both stale. It only refetches on
		// mount and on its own filter changes, and creating from here changes
		// neither — so if the user is standing on that tab it never reloads.
		refetchOverviewInstances();
		fetchSummary();
	};

	// Overview tab: server-side Country + Supplier + Due-date filters (all
	// dimensions the /request_instances list accepts). They write into the
	// overview redux filters, which BuyerComplianceOverviewPage forwards as query
	// params on its instance fetch (it groups the results by supplier client-side).
	// Search is AdvancedFilters' built-in input (also server-side).
	const overviewFilterConfig = useMemo(
		() =>
			buildBuyerComplianceOverviewFilterConfig(
				OVERVIEW_TARGET_COLLECTION,
				updateInstanceFilters,
				supplierFacilities,
				fetchSupplierFacilities,
				SUPPLIERS_FILTER_TARGET_COLLECTION,
				complianceRequests,
				fetchComplianceRequestOptions,
				REQUESTS_FILTER_TARGET_COLLECTION
			),
		[
			updateInstanceFilters,
			supplierFacilities,
			fetchSupplierFacilities,
			complianceRequests,
			fetchComplianceRequestOptions,
		]
	);
	// Requests tab: server-side Compliance Type, Status, Mandatory/Optional, Location,
	// Problem Type, and Due-date filters (all dimensions the /requests list accepts).
	// They write into the Requests redux filters, which PaginatedReduxTable forwards
	// as query params. Memoized because it now also feeds SelectedFilters (whose
	// effects key on config identity).
	const requestsFilterConfig = useMemo(
		() =>
			buildComplianceRequestsFilterConfig(
				REQUESTS_TARGET_COLLECTION,
				updateRequestFilters,
				complianceTypes,
				fetchComplianceTypes,
				TYPES_FILTER_TARGET_COLLECTION,
				locations,
				fetchMultipleLocations,
				LOCATIONS_FILTER_TARGET_COLLECTION,
				problemTypes,
				fetchProblemTypesForRequestsFilter,
				fetchMultipleProblemTypes,
				PROBLEM_TYPES_FILTER_TARGET_COLLECTION
			),
		[
			updateRequestFilters,
			complianceTypes,
			fetchComplianceTypes,
			locations,
			fetchMultipleLocations,
			problemTypes,
			fetchProblemTypesForRequestsFilter,
			fetchMultipleProblemTypes,
		]
	);

	const nonCompliantCount = nullSafeGet('nonCompliantSuppliers', summary);
	const totalSuppliers = nullSafeGet('totalSuppliers', summary);

	const checkSubTabActive = (key: string) => pathname.indexOf(`${basePath}/${key}`) !== -1;

	const activeSubTabKey = checkSubTabActive('requests') ? 'requests' : 'overview';
	const isRequestsTab = activeSubTabKey === 'requests';

	const handleSubTabChange = useCallback((key) => history.push(`${basePath}/${key}`), [history]);

	const renderBaseRedirect = () => <Redirect to={{ pathname: `${basePath}/overview` }} />;

	// Feature-flag gate (defense-in-depth for direct URLs — the entry tabs are already
	// hidden when off). basePath ends in '/compliance'; drop it to land on the parent
	// overview, which redirects to its default tab.
	if (!posthog.isFeatureEnabled(COMPLIANCE_FEATURE_FLAG)) {
		return <Redirect to={{ pathname: basePath.replace(COMPLIANCE_PATH_MARKER, '') }} />;
	}

	return (
		<div className="buyer-compliance-page px-4">
			<Route path={basePath} exact={true} render={renderBaseRedirect} />
			<div className="flex flex-row flex-wrap items-center gap-4 py-3">
				<OWSubTabs
					tabs={SUB_TABS.map((tab) => ({ key: tab.key, label: tab.name }))}
					activeKey={activeSubTabKey}
					onChange={handleSubTabChange}
				/>
				<AdvancedFilters
					key={isRequestsTab ? REQUESTS_TARGET_COLLECTION : OVERVIEW_TARGET_COLLECTION}
					filtersTargetCollectionName={
						isRequestsTab ? REQUESTS_TARGET_COLLECTION : OVERVIEW_TARGET_COLLECTION
					}
					filtersStateSlice={isRequestsTab ? complianceRequests : complianceRequestInstances}
					updateFilters={isRequestsTab ? updateRequestFilters : updateInstanceFilters}
					clearAndUpdateFilters={
						isRequestsTab ? clearAndUpdateRequestFilters : clearAndUpdateInstanceFilters
					}
					filterConfig={isRequestsTab ? requestsFilterConfig : overviewFilterConfig}
					filterPlaceHolder="Search"
				/>
				<div className="ml-auto flex flex-row items-center gap-3">
					{totalSuppliers ? (
						<span className="bodyregular">
							<span className="bodymedium underline" style={{ color: 'rgb(219, 123, 43)' }}>{`${
								nonCompliantCount || 0
							}/${totalSuppliers}`}</span>{' '}
							Suppliers Non-Compliant
						</span>
					) : null}
					<Tooltip title="Compliance settings">
						<Button size="large" icon={<SettingOutlined />} onClick={openSettingsModal} />
					</Tooltip>
					<Button size="large" onClick={openTypesModal}>
						Compliance Types
					</Button>
					<Button size="large" type="primary" onClick={openNewRequestModal}>
						+ Compliance Request
					</Button>
				</div>
			</div>
			{/* Removable filter pills. `containerClassName` puts the vertical spacing on
			    SelectedFilters' own root, which renders only when pills exist — so there's
			    no phantom gap under the header when no filters are set. */}
			{!isRequestsTab && (
				<SelectedFilters
					containerClassName="mb-3"
					filterConfig={overviewFilterConfig}
					filterValues={nullSafeGetOrElse(
						`${OVERVIEW_TARGET_COLLECTION}.filters`,
						complianceRequestInstances,
						{}
					)}
					updateFilters={(patch) => updateInstanceFilters(patch, OVERVIEW_TARGET_COLLECTION)}
				/>
			)}
			{isRequestsTab && (
				<SelectedFilters
					containerClassName="mb-3"
					filterConfig={requestsFilterConfig}
					filterValues={nullSafeGetOrElse(
						`${REQUESTS_TARGET_COLLECTION}.filters`,
						complianceRequests,
						{}
					)}
					updateFilters={(patch) => updateRequestFilters(patch, REQUESTS_TARGET_COLLECTION)}
				/>
			)}
			{typesModalOpen && (
				<ComplianceTypesModal visible={typesModalOpen} onCancel={closeTypesModal} />
			)}
			{newRequestModalOpen && (
				<NewComplianceRequestModal
					visible={newRequestModalOpen}
					onCancel={closeNewRequestModal}
					onSuccess={handleNewRequestSuccess}
				/>
			)}
			{settingsModalOpen && (
				<ComplianceSettingsModal visible={settingsModalOpen} onCancel={closeSettingsModal} />
			)}
			{childRoutes.map((route, index) => (
				<RouteWithSubRoutes key={index} {...route} />
			))}
		</div>
	);
};

const mapStateToProps = (state) => ({
	summary: nullSafeGetOrElse('counts.complianceSummary', state.compliance_request_instances, null),
	complianceRequestInstances: state.compliance_request_instances,
	complianceRequests: state.compliance_requests,
	complianceTypes: state.compliance_types,
	supplierFacilities: state.supplier_facilities,
	locations: state.locations,
	problemTypes: state.problem_types,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	// Role = the logged-in user's role (injected by withUserType from session), not
	// the build constant — so the buyer app hits /buyer and internal tech hits
	// /supplier regardless of how the bundle was built.
	// Replay the collection's current filters + pagination (read at call time via
	// getState) so creating a request keeps the Requests tab on its current filtered
	// page instead of snapping back to unfiltered page 1 under still-active pills.
	refetchComplianceRequests: () =>
		dispatch((innerDispatch, getState) => {
			const slice = getState().compliance_requests;
			const filters = nullSafeGetOrElse('buyerComplianceRequestsIndex.filters', slice, {});
			const pagination = nullSafeGetOrElse('buyerComplianceRequestsIndex.pagination', slice, {
				current: 1,
				pageSize: 10,
			});
			return innerDispatch(
				fetchComplianceRequestsWithInstanceCounts(ownProps.userType)(
					// First draft: country fixed to US (no UI filter).
					{ countryCode: 'US' },
					'buyerComplianceRequestsIndex',
					pagination,
					null,
					filters
				)
			);
		}),
	// Same replay-from-redux trick as the requests refetch above: read the Overview's
	// live filters at call time so the grid comes back on the page and filters the
	// user is actually looking at. Pagination, collection and count names come from
	// the Overview itself so the two cannot drift.
	refetchOverviewInstances: () =>
		dispatch((innerDispatch, getState) => {
			const slice = getState().compliance_request_instances;
			const filters = nullSafeGetOrElse(`${OVERVIEW_TARGET_COLLECTION}.filters`, slice, {});
			const instancesThunks = complianceRequestInstancesRestCrudThunks(ownProps.userType);
			innerDispatch(
				instancesThunks.read(
					{ countryCode: 'US' },
					OVERVIEW_TARGET_COLLECTION,
					OVERVIEW_FETCH_PAGINATION,
					null,
					filters
				)
			);
			return innerDispatch(
				instancesThunks.countBy(
					{ countBy: 'status', countryCode: 'US', ...omitStatusFilters(filters) },
					OVERVIEW_STATUS_COUNTS_NAME
				)
			);
		}),
	fetchSummary: () => dispatch(fetchComplianceSummary(ownProps.userType)()),
	fetchComplianceTypes: (params, targetCollectionName, pagination, sorting, filters, add) =>
		dispatch(
			complianceTypesRestCrudThunks(ownProps.userType).read(
				params,
				targetCollectionName || TYPES_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Overview supplier filter — private network (same source as the WO filters
	// and the New Request modal), not the raw supplier_facilities list.
	fetchSupplierFacilities: (params, targetCollectionName, pagination, sorting, filters, add) =>
		dispatch(
			fetchSupplierFacilitiesInPrivateNetwork(ownProps.userType)(
				params,
				targetCollectionName || SUPPLIERS_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Overview request filter — plain /requests read (the async select just needs
	// id + name, not the hydrated with_instance_counts payload).
	fetchComplianceRequestOptions: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		add
	) =>
		dispatch(
			complianceRequestsRestCrudThunks(ownProps.userType).read(
				params,
				targetCollectionName || REQUESTS_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Requests location filter — hierarchical buyer locations (same source as the
	// New Request modal + WO filters). Primes/backs the RegionsLocationsTreeSelect.
	fetchLocationsForRequestsFilter: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		add
	) =>
		dispatch(
			fetchLocationsWithRegionsAndSubRegions(ownProps.userType)(
				params,
				targetCollectionName || LOCATIONS_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Hydrates the selected location pills/tags by id.
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
	// Requests problem-type filter — hierarchical tree (with_children), not the flat
	// list. Backs the ProblemTypeTreeSelect and primes its collection.
	fetchProblemTypesForRequestsFilter: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		add
	) =>
		dispatch(
			problemTypesHeirarchical(ownProps.userType)(
				params,
				targetCollectionName || PROBLEM_TYPES_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Hydrates the selected problem-type pills/tags by id.
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
	updateRequestFilters: (filters, targetCollectionName) =>
		dispatch(COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)),
	clearAndUpdateRequestFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUESTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	// Search + compliance filter values persist in the overview target
	// collection's redux filters; the overview reads and applies them client-side.
	updateInstanceFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	clearAndUpdateInstanceFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(BuyerCompliancePage))
);
